import React, { useState, useEffect } from 'react';
import './Inbox.css';

const Inbox = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Mock data - replace with actual API call
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        // Simulate API call to your SQL database
        // const response = await axios.get('/api/messages');
        
        // Mock data for demonstration
        const mockMessages = [
          {
            id: 1,
            phone: '+27665288667',
            message: 'Hello, I need help with setting up WhatsApp Business API',
            direction: 'incoming',
            timestamp: '2024-01-20T10:30:00Z',
            status: 'read',
            customerName: 'John Doe',
            templateUsed: null
          },
          {
            id: 2,
            phone: '+27665288667',
            message: 'Your order #12345 has been confirmed and will be delivered tomorrow',
            direction: 'outgoing',
            timestamp: '2024-01-20T10:35:00Z',
            status: 'delivered',
            customerName: 'John Doe',
            templateUsed: 'Order Confirmation'
          },
          {
            id: 3,
            phone: '+27723456789',
            message: 'Hi, I want to book a demo for your AI services',
            direction: 'incoming',
            timestamp: '2024-01-20T11:15:00Z',
            status: 'unread',
            customerName: 'Jane Smith',
            templateUsed: null
          },
          {
            id: 4,
            phone: '+27723456789',
            message: 'Thank you for your interest! Our team will contact you within 24 hours.',
            direction: 'outgoing',
            timestamp: '2024-01-20T11:20:00Z',
            status: 'delivered',
            customerName: 'Jane Smith',
            templateUsed: 'Auto Response'
          },
          {
            id: 5,
            phone: '+27812345678',
            message: 'Can you help me integrate WhatsApp with my CRM system?',
            direction: 'incoming',
            timestamp: '2024-01-20T12:00:00Z',
            status: 'unread',
            customerName: 'Mike Johnson',
            templateUsed: null
          }
        ];
        
        setMessages(mockMessages);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching messages:', error);
        setLoading(false);
      }
    };

    fetchMessages();
  }, []);

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const filteredMessages = messages.filter(message => {
    const matchesFilter = filter === 'all' || 
                         (filter === 'incoming' && message.direction === 'incoming') ||
                         (filter === 'outgoing' && message.direction === 'outgoing') ||
                         (filter === 'unread' && message.status === 'unread');
    
    const matchesSearch = message.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         message.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         message.phone.includes(searchTerm);
    
    return matchesFilter && matchesSearch;
  });

  const handleMessageClick = (message) => {
    setSelectedMessage(message);
    // Mark as read if it's unread
    if (message.status === 'unread') {
      setMessages(prev => prev.map(msg => 
        msg.id === message.id ? { ...msg, status: 'read' } : msg
      ));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'read': return '#10b981';
      case 'unread': return '#f59e0b';
      case 'delivered': return '#3b82f6';
      case 'failed': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getDirectionIcon = (direction) => {
    return direction === 'incoming' ? '📥' : '📤';
  };

  const unreadCount = messages.filter(msg => msg.status === 'unread').length;

  if (loading) {
    return (
      <div className="inbox-loading">
        <div className="spinner"></div>
        <p>Loading messages...</p>
      </div>
    );
  }

  return (
    <div className="inbox-container">
      <div className="inbox-header">
        <div className="header-left">
          <h1>Messages Inbox</h1>
          {unreadCount > 0 && (
            <span className="unread-badge">{unreadCount} unread</span>
          )}
        </div>
        
        <div className="header-right">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search messages..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="filter-buttons">
            <button 
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All
            </button>
            <button 
              className={`filter-btn ${filter === 'incoming' ? 'active' : ''}`}
              onClick={() => setFilter('incoming')}
            >
              Incoming
            </button>
            <button 
              className={`filter-btn ${filter === 'outgoing' ? 'active' : ''}`}
              onClick={() => setFilter('outgoing')}
            >
              Outgoing
            </button>
            <button 
              className={`filter-btn ${filter === 'unread' ? 'active' : ''}`}
              onClick={() => setFilter('unread')}
            >
              Unread
            </button>
          </div>
        </div>
      </div>

      <div className="inbox-content">
        <div className="messages-list">
          {filteredMessages.length === 0 ? (
            <div className="no-messages">
              <div className="no-messages-icon">📭</div>
              <h3>No messages found</h3>
              <p>Try adjusting your search or filters</p>
            </div>
          ) : (
            filteredMessages.map(message => (
              <div
                key={message.id}
                className={`message-item ${message.status === 'unread' ? 'unread' : ''} ${selectedMessage?.id === message.id ? 'selected' : ''}`}
                onClick={() => handleMessageClick(message)}
              >
                <div className="message-header">
                  <div className="message-info">
                    <span className="direction-icon">{getDirectionIcon(message.direction)}</span>
                    <div className="contact-info">
                      <h4>{message.customerName}</h4>
                      <p className="phone-number">{message.phone}</p>
                    </div>
                  </div>
                  <div className="message-meta">
                    <span className="timestamp">{formatTimestamp(message.timestamp)}</span>
                    <span 
                      className="status-dot"
                      style={{ backgroundColor: getStatusColor(message.status) }}
                    ></span>
                  </div>
                </div>
                
                <div className="message-content">
                  <p>{message.message}</p>
                  {message.templateUsed && (
                    <span className="template-tag">Template: {message.templateUsed}</span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {selectedMessage && (
          <div className="message-detail">
            <div className="detail-header">
              <div className="contact-detail">
                <h3>{selectedMessage.customerName}</h3>
                <p className="phone-detail">{selectedMessage.phone}</p>
              </div>
              <div className="detail-meta">
                <span className="direction-detail">
                  {getDirectionIcon(selectedMessage.direction)} {selectedMessage.direction}
                </span>
                <span className="time-detail">
                  {new Date(selectedMessage.timestamp).toLocaleString()}
                </span>
              </div>
            </div>
            
            <div className="detail-content">
              <div className="message-bubble">
                <p>{selectedMessage.message}</p>
              </div>
              
              {selectedMessage.templateUsed && (
                <div className="template-info">
                  <strong>Template Used:</strong> {selectedMessage.templateUsed}
                </div>
              )}
            </div>
            
            <div className="detail-actions">
              <button className="reply-btn">Reply</button>
              <button className="forward-btn">Forward</button>
              <button className="delete-btn">Delete</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Inbox;
